import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
AppBar,
Toolbar,
IconButton,
Menu,
MenuItem,
Divider,
Avatar,
Typography,
Box,
styled,
useMediaQuery,
useTheme,
Slide,
Badge,
Button,
Paper,
ListItemIcon,
ListItemText,
Chip,
alpha,
CircularProgress
} from '@mui/material';
import { 
Menu as MenuIcon,
AccountCircle,
Dashboard as DashboardIcon,
Logout,
Person,
Settings,
Science,
Work,
ShoppingBag,
ExpandMore,
Email,
Phone,
School,
Feedback,
PublicOffRounded
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { clearLocalStorageData } from '../../utilities/Functions';
import { MoreLinks, ProductsLinks, ProjectsLinks, PublicationLinks, ResearchLinks } from './Data';
import { BASE_URL } from '../../utilities/Env';
import logoIcone from "../../utilities/imgs/logo.png";

// Styled components (keeping existing ones)
const LogoContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    textDecoration: 'none',
    color: 'inherit',
}));

const LogoImage = styled('img')(({ theme }) => ({
    height: 40,
    width: 'auto',
    borderRadius: 10,
    objectFit: 'contain',
    [theme.breakpoints.down('md')]: {
        height: 35,
    },
}));

const LogoText = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    background: 'linear-gradient(135deg, #fff 0%, #e0e0e0 100%)',
    backgroundClip: 'text',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    [theme.breakpoints.down('md')]: {
        fontSize: '1.25rem',
    },
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(1, 2),
    [theme.breakpoints.up('md')]: {
        padding: theme.spacing(1, 4),
    },
}));

const NavItemsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('md')]: {
        display: 'none',
    },
}));

const MobileMenuButton = styled(IconButton)(({ theme }) => ({
    display: 'none',
    [theme.breakpoints.down('md')]: {
        display: 'flex',
    },
}));

const ProfileContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    cursor: 'pointer',
    padding: theme.spacing(1),
    borderRadius: theme.spacing(1),
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.1),
    },
}));

const NavButton = styled(Button)(({ theme }) => ({
    color: theme.palette.common.white,
    textTransform: 'none',
    fontWeight: 500,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(1, 2),
    '&:hover': {
        backgroundColor: alpha(theme.palette.common.white, 0.1),
        transform: 'translateY(-1px)',
    },
    transition: 'all 0.3s ease',
}));

const DropdownMenu = styled(Menu)(({ theme }) => ({
    '& .MuiPaper-root': {
        minWidth: 280,
        borderRadius: theme.spacing(2),
        boxShadow: theme.shadows[8],
        marginTop: theme.spacing(1),
        background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
        backdropFilter: 'blur(20px)',
    },
}));

const UserInfoCard = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    margin: theme.spacing(1),
    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
}));

const DropdownContainer = styled(Box)(({ theme }) => ({
    position: 'relative',
    display: 'inline-block',
}));

const DropdownContent = styled(motion.div)(({ theme }) => ({
    position: 'absolute',
    top: '100%',
    left: 0,
    minWidth: 240,
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.95)} 100%)`,
    backdropFilter: 'blur(20px)',
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows[8],
    padding: theme.spacing(1),
    zIndex: 1000,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
    '& .MuiTypography-root': {
        color: theme.palette.text.primary,
    },
    '& .MuiListItemText-primary': {
        color: theme.palette.text.primary,
    },
    '& .MuiDivider-root': {
        backgroundColor: alpha(theme.palette.primary.main, 0.3),
    },
    '& .MuiMenuItem-root': {
        color: theme.palette.text.primary,
        '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.1),
            '& .MuiListItemText-primary': {
                color: theme.palette.primary.main,
                fontWeight: 600,
            },
            '& .MuiListItemIcon-root': {
                color: theme.palette.primary.main,
            },
        },
    },
    '& .MuiListItemIcon-root': {
        color: theme.palette.text.secondary,
        minWidth: theme.spacing(4),
    },
}));

const LevelChip = styled(Chip)(({ theme, level }) => {
    const colorMap = {
        'Professor': theme.palette.error.main,
        'Researcher': theme.palette.success.main,
        'Student': theme.palette.info.main,
        'Collaborator': theme.palette.warning.main,
    };

    return {
        backgroundColor: colorMap[level] || theme.palette.grey[500],
        color: theme.palette.common.white,
        fontWeight: 'bold',
        fontSize: '0.7rem',
        height: 20,
    };
});

const DropdownTitle = styled(Typography)(({ theme }) => ({
    color: theme.palette.primary.main,
    fontWeight: 'bold',
    fontSize: '0.875rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
}));

const DropdownMenuItem = styled(MenuItem)(({ theme }) => ({
    borderRadius: theme.spacing(1),
    margin: theme.spacing(0.5),
    padding: theme.spacing(1.5, 2),
    transition: 'all 0.2s ease',
    '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
        transform: 'translateX(4px)',
    },
}));

const CustomDivider = styled(Divider)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.primary.main, 0.3),
    margin: theme.spacing(1, 0),
    height: '2px',
}));

function NavigationMenu({ userData: propUserData }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();
    const [userData, setUserData] = useState(null);
    const [profileImage, setProfileImage] = useState(null);
    const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
    const [profileMenuAnchor, setProfileMenuAnchor] = useState(null);
    const [scrolled, setScrolled] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [imageError, setImageError] = useState(false);
    const [imageLoading, setImageLoading] = useState(true);

    useEffect(() => {
        loadUserData();
        
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        
        // Set up storage event listener to update user data when it changes
        const handleStorageChange = () => {
            loadUserData();
        };
        window.addEventListener('storage', handleStorageChange);
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
            window.removeEventListener('storage', handleStorageChange);
        };
    }, []);

    // Update when propUserData changes
    useEffect(() => {
        if (propUserData) {
            setUserData(propUserData);
        }
    }, [propUserData]);

    const loadUserData = () => {
        // Load researcher data
        const user = sessionStorage.getItem('reseacher');
        if (user) {
            const jsonData = JSON.parse(user);
            setUserData(jsonData);
        }
        
        // Load profile image separately
        const profileImg = sessionStorage.getItem('profile');
        console.log('Loaded profile image from storage:', profileImg);
        setProfileImage(profileImg);
        setImageError(false);
        setImageLoading(true);
    };

    const logout = () => {
        clearLocalStorageData();
        navigate('/');
        handleProfileMenuClose();
    };

    const handleMobileMenuOpen = (event) => {
        setMobileMenuAnchor(event.currentTarget);
    };

    const handleMobileMenuClose = () => {
        setMobileMenuAnchor(null);
    };

    const handleProfileMenuOpen = (event) => {
        setProfileMenuAnchor(event.currentTarget);
    };

    const handleProfileMenuClose = () => {
        setProfileMenuAnchor(null);
    };

    const handleDropdownEnter = (dropdownName) => {
        setActiveDropdown(dropdownName);
    };

    const handleDropdownLeave = () => {
        setActiveDropdown(null);
    };

    const getIconForCategory = (category) => {
        switch (category) {
            case 'Research': return <Science sx={{ fontSize: 18 }} />;
            case 'Projects': return <Work sx={{ fontSize: 18 }} />;
            case 'Products': return <ShoppingBag sx={{ fontSize: 18 }} />;
            default: return <Science sx={{ fontSize: 18 }} />;
        }
    };

    const getProfileImageUrl = () => {
        console.log('Getting profile image URL for:', profileImage);
        
        if (!profileImage || profileImage === 'pendding' || profileImage === 'pending' || profileImage === 'null') {
            console.log('No valid profile image found');
            return null;
        }
        
        // Check if the image URL is already absolute
        if (profileImage.startsWith('http://') || profileImage.startsWith('https://')) {
            console.log('Using absolute URL:', profileImage);
            return profileImage;
        }
        
        // If it starts with /images/, construct URL
        if (profileImage.startsWith('/images/')) {
            const fullUrl = `${BASE_URL}${profileImage}`;
            console.log('Constructed URL from path:', fullUrl);
            return fullUrl;
        }
        
        // Otherwise, assume it's just the filename
        const fullUrl = `${BASE_URL}/images/researcher/${profileImage}`;
        console.log('Constructed URL from filename:', fullUrl);
        return fullUrl;
    };

    const handleImageError = () => {
        console.error('Failed to load profile image:', profileImage);
        setImageError(true);
        setImageLoading(false);
    };

    const handleImageLoad = () => {
        console.log('Profile image loaded successfully');
        setImageLoading(false);
        setImageError(false);
    };

    const renderDropdownMenu = (title, items) => (
        <DropdownContainer
            onMouseEnter={() => handleDropdownEnter(title)}
            onMouseLeave={handleDropdownLeave}
        >
            <NavButton
                endIcon={<ExpandMore sx={{ 
                    fontSize: 18,
                    transform: activeDropdown === title ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease'
                }} />}
                sx={{
                    backgroundColor: activeDropdown === title ? alpha(theme.palette.common.white, 0.15) : 'transparent',
                }}
            >
                {title}
            </NavButton>

            <AnimatePresence>
                {activeDropdown === title && (
                    <DropdownContent
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                    >
                        <DropdownTitle variant="subtitle2" sx={{ px: 2, py: 1 }}>
                            {title}
                        </DropdownTitle>
                        <CustomDivider />
                        {items.map((item, index) => (
                            <DropdownMenuItem
                                key={index}
                                component={Link}
                                to={item.path}
                                onClick={handleDropdownLeave}
                            >
                                <ListItemIcon>
                                    {getIconForCategory(title)}
                                </ListItemIcon>
                                <ListItemText 
                                    primary={item.name}
                                    primaryTypographyProps={{
                                        fontSize: '0.9rem',
                                        fontWeight: 500,
                                    }}
                                />
                            </DropdownMenuItem>
                        ))}
                    </DropdownContent>
                )}
            </AnimatePresence>
        </DropdownContainer>
    );

    const renderMobileMenu = (
        <DropdownMenu
            anchorEl={mobileMenuAnchor}
            open={Boolean(mobileMenuAnchor)}
            onClose={handleMobileMenuClose}
        >
            <MenuItem component={Link} to="/dashboard" onClick={handleMobileMenuClose}>
                <DashboardIcon sx={{ mr: 2 }} />
                Dashboard
            </MenuItem>
            <Divider />

            {[
                { title: 'Research', items: ResearchLinks, icon: <Science /> },
                { title: 'Publications', items: PublicationLinks, icon: <PublicOffRounded /> },
                { title: 'Projects', items: ProjectsLinks, icon: <Work /> },
                { title: 'Products', items: ProductsLinks, icon: <ShoppingBag /> },
                { title: 'More', items: MoreLinks, icon: <Feedback /> },
            ].map((section, index) => (
                <React.Fragment key={section.title}>
                    <Typography variant="subtitle2" sx={{ 
                        px: 2, 
                        py: 1, 
                        color: 'primary.main', 
                        fontWeight: 'bold',
                        fontSize: '0.75rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        {section.title}
                    </Typography>
                    {section.items.map((item, itemIndex) => (
                        <MenuItem 
                            key={itemIndex}
                            component={Link}
                            to={item.path}
                            onClick={handleMobileMenuClose}
                            sx={{
                                '& .MuiListItemText-primary': {
                                    color: theme.palette.text.primary,
                                }
                            }}
                        >
                            <ListItemIcon>{section.icon}</ListItemIcon>
                            <ListItemText primary={item.name} />
                        </MenuItem>
                    ))}
                    {index < 2 && <Divider />}
                </React.Fragment>
            ))}
        </DropdownMenu>
    );

    const renderProfileMenu = (
        <DropdownMenu
            anchorEl={profileMenuAnchor}
            open={Boolean(profileMenuAnchor)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
            <UserInfoCard elevation={0}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        variant="dot"
                        color="success"
                    >
                        <Avatar
                            src={getProfileImageUrl()}
                            alt={userData?.name || 'User'}
                            sx={{ width: 56, height: 56 }}
                            onError={handleImageError}
                            onLoad={handleImageLoad}
                        >
                            {(!getProfileImageUrl() || imageError) && (
                                <AccountCircle sx={{ fontSize: 40 }} />
                            )}
                        </Avatar>
                    </Badge>
                    <Box>
                        <Typography variant="h6" fontWeight="bold" color="text.primary">
                            {userData?.name || 'User'}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {userData?.title || 'Researcher'}
                        </Typography>
                        {userData?.level && userData.level !== 'pendding' && (
                            <LevelChip label={userData.level} size="small" level={userData.level} />
                        )}
                    </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    {userData?.email && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Email sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="caption" color="text.primary">{userData.email}</Typography>
                        </Box>
                    )}
                    {userData?.contact && userData.contact !== 'pendding' && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Phone sx={{ fontSize: 16, color: 'primary.main' }} />
                            <Typography variant="caption" color="text.primary">{userData.contact}</Typography>
                        </Box>
                    )}
                </Box>
            </UserInfoCard>

            <Divider />

            <MenuItem component={Link} to="/profile" onClick={handleProfileMenuClose}>
                <ListItemIcon><Person fontSize="small" /></ListItemIcon>
                <ListItemText primary="My Profile" />
            </MenuItem>

            <Divider />

            <MenuItem onClick={logout}>
                <ListItemIcon><Logout fontSize="small" /></ListItemIcon>
                <ListItemText primary="Log Out" />
            </MenuItem>
        </DropdownMenu>
    );

    return (
        <Slide direction="down" in={!scrolled} appear={false}>
            <AppBar 
                position="fixed" 
                sx={{
                    backgroundColor: scrolled 
                        ? alpha(theme.palette.primary.main, 0.92) 
                        : theme.palette.primary.main,
                    backdropFilter: scrolled ? 'blur(20px)' : 'none',
                    transition: 'all 0.3s ease',
                    boxShadow: scrolled ? theme.shadows[8] : 'none',
                    backgroundImage: scrolled 
                        ? `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.92)} 0%, ${alpha(theme.palette.secondary.main, 0.92)} 100%)`
                        : 'none',
                }}
            >
                <StyledToolbar>
                    {/* Mobile menu button */}
                    <MobileMenuButton
                        size="large"
                        edge="start"
                        color="inherit"
                        aria-label="menu"
                        onClick={handleMobileMenuOpen}
                    >
                        <MenuIcon />
                    </MobileMenuButton>

                    {/* Logo/Brand */}
                    <LogoContainer component={Link} to="/dashboard">
                        <LogoImage src={logoIcone} alt="RIA Logo" />
                        <LogoText variant="h6">
                            RIA
                        </LogoText>
                    </LogoContainer>

                    {/* Navigation items */}
                    <NavItemsContainer>
                        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <NavButton component={Link} to="/dashboard" startIcon={<DashboardIcon />}>
                                Dashboard
                            </NavButton>
                        </motion.div>

                        {renderDropdownMenu('Research', ResearchLinks)}
                        {renderDropdownMenu('Publications', PublicationLinks)}
                        {renderDropdownMenu('Projects', ProjectsLinks)}
                        {renderDropdownMenu('Products', ProductsLinks)}
                        {renderDropdownMenu('More', MoreLinks)}
                    </NavItemsContainer>

                    {/* Profile section */}
                    {userData ? (
                        <ProfileContainer onClick={handleProfileMenuOpen}>
                            <Box sx={{ textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
                                <Typography variant="subtitle2" fontWeight="bold">
                                    {userData.name}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                    {userData.title || 'Researcher'}
                                </Typography>
                            </Box>

                            <Badge
                                overlap="circular"
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                variant="dot"
                                color="success"
                            >
                                <Avatar
                                    src={getProfileImageUrl()}
                                    alt={userData.name}
                                    sx={{ 
                                        width: 40, 
                                        height: 40,
                                        border: `2px solid ${alpha(theme.palette.common.white, 0.3)}`
                                    }}
                                    onError={handleImageError}
                                    onLoad={handleImageLoad}
                                >
                                    {(!getProfileImageUrl() || imageError) && (
                                        <AccountCircle />
                                    )}
                                </Avatar>
                            </Badge>
                        </ProfileContainer>
                    ) : (
                        <Button color="inherit" component={Link} to="/">
                            Login
                        </Button>
                    )}
                </StyledToolbar>

                {/* Menus */}
                {renderMobileMenu}
                {renderProfileMenu}
            </AppBar>
        </Slide>
    );
}

export default NavigationMenu;